import React from 'react';
import { titles, countries, industries } from '../../utils/form-dropdowns';

const inputCls = (hasError) =>
  `w-full border rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-bahyellow ${hasError ? 'border-red-500' : 'border-gray-300'}`;

function Field({ label, required, error, children, className }) {
  return (
    <div className={`flex flex-col gap-1 ${className || ''}`}>
      <label className="text-sm font-medium text-gray-700">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <span className="text-red-500 text-sm">{error}</span>}
    </div>
  );
}

export default class JoinUsForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      formFields: {
        salutation: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        nationality: '',
        telephone: '',
        email: '',
        jobTitle: '',
        company: '',
        address: '',
        vatNumber: '',
        industry: '',
        website: '',
        acceptsRoARoIO: false,
        acceptsPrivacyPolicy: false,
      },
      validationErrors: {
        salutation: null,
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        nationality: null,
        telephone: null,
        email: null,
        jobTitle: null,
        company: null,
        address: null,
        vatNumber: null,
        industry: null,
        website: null,
        acceptsRoARoIO: null,
        acceptsPrivacyPolicy: null,
      },
      isLoading: false,
      responseStatus: null,
    };
  }

  handleInputChange = (field, value) => {
    const validationErrorMessage = this.getValidationErrorMessage(field, value);
    this.setState({
      formFields: { ...this.state.formFields, [field]: value },
      validationErrors: { ...this.state.validationErrors, [field]: validationErrorMessage },
    });
  }

  getValidationErrorMessage = (field, value) => {
    switch (field) {
      case 'salutation':
        return !value ? 'Salutation is required' : (titles.indexOf(value) < 0 ? 'Invalid salutation' : null);
      case 'firstName':
        return !value ? 'First name is required' : null;
      case 'lastName':
        return !value ? 'Family name is required' : null;
      case 'dateOfBirth':
        return !value ? 'Date of birth is required' : null;
      case 'nationality':
        return !value ? 'Nationality is required' : (countries.indexOf(value) < 0 ? 'Invalid nationality' : null);
      case 'telephone':
        return !value ? 'Telephone is required' : null;
      case 'email':
        return !value ? 'E-mail is required' : (!/^\S+@\S+$/.test(value) ? 'Invalid e-mail' : null);
      case 'jobTitle':
        return !value ? 'Job title is required' : null;
      case 'company':
        return !value ? 'Company name is required' : null;
      case 'address':
        return !value ? 'Address is required' : null;
      case 'vatNumber':
        return !value ? 'VAT number is required' : null;
      case 'industry':
        return !value ? 'Industry is required' : (industries.indexOf(value) < 0 ? 'Invalid industry' : null);
      case 'website':
        return !value ? 'Website is required' : null;
      case 'acceptsRoARoIO':
        return !value ? 'Accepting the Rules of Association and the Rules of Internal Order is required' : null;
      case 'acceptsPrivacyPolicy':
        return !value ? 'Accepting the privacy policy is required' : null;
      default:
        return null;
    }
  }

  handleSubmit = (event) => {
    event.preventDefault();
    this.setState((state) => {
      let formHasValidationErrors = false;
      const validationErrors = Object.keys(state.validationErrors).reduce((acc, field) => {
        const msg = this.getValidationErrorMessage(field, state.formFields[field]);
        if (msg) formHasValidationErrors = true;
        return { ...acc, [field]: msg };
      }, {});
      return { validationErrors, isLoading: !formHasValidationErrors };
    }, () => {
      if (!this.state.isLoading) return;
      fetch(`${process.env.REACT_APP_API_URL}/join-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.state.formFields),
      })
        .then((response) => response.text().then((data) => {
          const validationErrors = data ? JSON.parse(data).validationErrors : {};
          this.setState((state) => ({
            validationErrors: { ...state.validationErrors, ...validationErrors },
            isLoading: false,
            responseStatus: response.status,
          }));
        }))
        .catch(() => this.setState({ isLoading: false, responseStatus: 500 }));
    });
  }

  render() {
    const { formFields: f, validationErrors: e } = this.state;
    return (
      <>
        {(!this.state.responseStatus || this.state.responseStatus === 400) && (
          <form onSubmit={this.handleSubmit} className="flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-4 max-w-2xl">

              <h3 className="mt-5 text-lg font-semibold text-gray-900">Personal info</h3>

              {/* Salutation · First name · Family name */}
              <div className="flex gap-3">
                <Field label="Salutation" required error={e.salutation} className="w-28 shrink-0">
                  <select id="salutation" value={f.salutation} onChange={(ev) => this.handleInputChange('salutation', ev.target.value)} className={inputCls(e.salutation)}>
                    {!f.salutation && <option value=""></option>}
                    {titles.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="First name" required error={e.firstName} className="flex-1">
                  <input id="firstName" type="text" value={f.firstName} onChange={(ev) => this.handleInputChange('firstName', ev.target.value)} className={inputCls(e.firstName)} />
                </Field>
                <Field label="Family name" required error={e.lastName} className="flex-1">
                  <input id="lastName" type="text" value={f.lastName} onChange={(ev) => this.handleInputChange('lastName', ev.target.value)} className={inputCls(e.lastName)} />
                </Field>
              </div>

              {/* Date of birth · Nationality */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Date of birth" required error={e.dateOfBirth}>
                  <input id="dateOfBirth" type="date" value={f.dateOfBirth} onChange={(ev) => this.handleInputChange('dateOfBirth', ev.target.value)} className={inputCls(e.dateOfBirth)} />
                </Field>
                <Field label="Nationality" required error={e.nationality}>
                  <select id="nationality" value={f.nationality} onChange={(ev) => this.handleInputChange('nationality', ev.target.value)} className={inputCls(e.nationality)}>
                    {!f.nationality && <option value=""></option>}
                    {countries.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>

              {/* Telephone · E-mail */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Telephone" required error={e.telephone}>
                  <input id="telephone" type="tel" value={f.telephone} onChange={(ev) => this.handleInputChange('telephone', ev.target.value)} className={inputCls(e.telephone)} />
                </Field>
                <Field label="E-mail" required error={e.email}>
                  <input id="email" type="email" value={f.email} onChange={(ev) => this.handleInputChange('email', ev.target.value)} className={inputCls(e.email)} />
                </Field>
              </div>

              {/* Job title */}
              <Field label="Job title" required error={e.jobTitle}>
                <input id="jobTitle" type="text" value={f.jobTitle} onChange={(ev) => this.handleInputChange('jobTitle', ev.target.value)} className={inputCls(e.jobTitle)} />
              </Field>

              <h3 className="mt-5 text-lg font-semibold text-gray-900">Company info</h3>

              {/* Company name · VAT */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company name" required error={e.company}>
                  <input id="company" type="text" value={f.company} onChange={(ev) => this.handleInputChange('company', ev.target.value)} className={inputCls(e.company)} />
                </Field>
                <Field label="VAT number" required error={e.vatNumber}>
                  <input id="vatNumber" type="text" value={f.vatNumber} onChange={(ev) => this.handleInputChange('vatNumber', ev.target.value)} className={inputCls(e.vatNumber)} />
                </Field>
              </div>

              {/* Address */}
              <Field label="Address" required error={e.address}>
                <textarea id="address" rows={3} value={f.address} onChange={(ev) => this.handleInputChange('address', ev.target.value)} className={inputCls(e.address)} />
              </Field>

              {/* Website */}
              <Field label="Website" required error={e.website}>
                <input id="website" type="text" value={f.website} onChange={(ev) => this.handleInputChange('website', ev.target.value)} className={inputCls(e.website)} />
              </Field>

              {/* Industry */}
              <Field label="Industry" required error={e.industry}>
                <select id="industry" value={f.industry} onChange={(ev) => this.handleInputChange('industry', ev.target.value)} className={inputCls(e.industry)}>
                  {!f.industry && <option value=""></option>}
                  {industries.map((ind) => <option key={ind}>{ind}</option>)}
                </select>
              </Field>

            </div>{/* end max-w-2xl */}

            {/* Checkboxes */}
            <div className="flex items-start gap-3 mt-2">
              <input
                id="acceptsRoARoIO"
                type="checkbox"
                checked={f.acceptsRoARoIO}
                onChange={(ev) => this.handleInputChange('acceptsRoARoIO', ev.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-bahyellow"
              />
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700" htmlFor="acceptsRoARoIO">
                  I confirm that I have read the{' '}
                  <a href="http://files.brusselsafricahub.eu/rules-of-association.pdf" className="text-blue-500" target="_blank" rel="noopener noreferrer">Rules of Association</a>
                  {' '}and the{' '}
                  <a href="http://files.brusselsafricahub.eu/rules-of-internal-order.pdf" className="text-blue-500" target="_blank" rel="noopener noreferrer">Rules of Internal Order</a>
                  {' '}and I agree to abide by those rules, in particular to the Statutes. Title II, art. 6 to 10 and the rules of internal order{' '}
                  <span className="text-red-500">*</span>
                </label>
                {e.acceptsRoARoIO && <span className="text-red-500 text-sm">{e.acceptsRoARoIO}</span>}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                id="acceptsPrivacyPolicy"
                type="checkbox"
                checked={f.acceptsPrivacyPolicy}
                onChange={(ev) => this.handleInputChange('acceptsPrivacyPolicy', ev.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-bahyellow"
              />
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700" htmlFor="acceptsPrivacyPolicy">
                  I accept that the above information is stored &amp; used by the Brussels-Africa Hub to inform me about news, events, activities and services of the Brussels-Africa Hub, possibly in cooperation with partners{' '}
                  <span className="text-red-500">*</span>
                </label>
                {e.acceptsPrivacyPolicy && <span className="text-red-500 text-sm">{e.acceptsPrivacyPolicy}</span>}
              </div>
            </div>

            {this.state.isLoading ? (
              <button type="button" className="cursor-not-allowed mt-4 rounded-full bg-bahyellow px-6 py-3 font-semibold opacity-60 flex items-center gap-2 self-start">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending…
              </button>
            ) : (
              <button type="submit" className="mt-4 rounded-full bg-bahyellow px-6 py-3 font-semibold hover:opacity-90 transition-opacity self-start">
                Submit
              </button>
            )}
          </form>
        )}
        {this.state.responseStatus === 200 && (
          <span className="block text-green-600">Your message has been received. We will get back to you as soon as possible.</span>
        )}
        {this.state.responseStatus && this.state.responseStatus !== 200 && this.state.responseStatus !== 400 && (
          <span className="block text-red-500">Something went wrong. Please email us at <a href="mailto:info@brusselsafricahub.eu">info@brusselsafricahub.eu</a>.</span>
        )}
      </>
    );
  }
}
